import Phaser from 'phaser';
import { TILE_SIZE, GAME_WIDTH, GAME_HEIGHT } from '../game/constants';
import { runState } from '../services/RunState';
import { profileStore } from '../services/ProfileStore';
import { gameBridge } from '../services/GameBridge';
import { WORLD_TILES, SUBJECT_THEMES, randomElement, getCharacterOption } from '../config/tileIndex';
import { Spawner } from '../game/Spawner';
import { TILE_OVERRIDES, PROPS_MAPPING } from '../config/assetMapping';
import { DialogManager } from '../lib/DialogManager';

interface DoorData {
  x: number;
  y: number;
  subject: string;
  color: number;
  zone: Phaser.GameObjects.Zone;
  visual: Phaser.GameObjects.Sprite;
  label: Phaser.GameObjects.Text;
  monster?: Phaser.GameObjects.Sprite;
}

interface JusticeNPC {
  id: string;
  name: string;
  sprite: Phaser.GameObjects.Sprite;
  quotes: string[];
  // Wandering state
  wanderTimer?: Phaser.Time.TimerEvent;
  targetX?: number;
  targetY?: number;
  isWandering?: boolean;
  homeX?: number;
  homeY?: number;
}

interface JusticeQuoteData {
  name: string;
  title: string;
  quotes: Array<{ text: string; context?: string; date?: string }>;
}

const SCALE = 2; // Scale up 16px sprites to 32px for better fit
const SCALED_TILE = TILE_SIZE * SCALE;

/**
 * World Scene - Hub map with subject doors
 * Player navigates the hub and enters doors to start quiz runs
 * Now with proper sprite rendering!
 */
export class WorldScene extends Phaser.Scene {
  private player!: Phaser.GameObjects.Sprite;
  private cursors!: Phaser.Types.Input.Keyboard.CursorKeys;
  private walls!: Phaser.Physics.Arcade.StaticGroup;
  private doors: DoorData[] = [];
  private playerSpeed = 160;
  private mapData: any;
  private activeDoor: DoorData | null = null;
  private canEnterDoor = true;
  private lastDirection: 'up' | 'down' | 'left' | 'right' = 'down';

  // Layers for proper depth sorting
  private floorLayer!: Phaser.GameObjects.Container;
  private propLayer!: Phaser.GameObjects.Container;
  private entityLayer!: Phaser.GameObjects.Container;
  private charId!: string;
  
  // Justice NPC interaction
  private justiceNPCs: JusticeNPC[] = [];
  private dialogManager!: DialogManager;

  constructor() {
    super({ key: 'WorldScene' });
  }

  create(): void {
    console.info('[WorldScene] create() started');
    // End any active run when returning to hub
    if (runState.isRunActive()) {
      runState.endRun();
    }

    this.charId = profileStore.getSelectedCharacter();
    
    // Create layer containers for depth sorting
    this.floorLayer = this.add.container(0, 0);
    this.propLayer = this.add.container(0, 0);
    this.entityLayer = this.add.container(0, 0);
    
    // Create dialog manager
    this.dialogManager = new DialogManager(this);

    // Load map data from Phaser cache (loaded in PreloadScene)
    this.mapData = this.cache.json.get('hub-map');
    if (!this.mapData) {
      console.error('[WorldScene] hub-map.json not found in cache!');
      return;
    }
    
    this.createTilemap();
    this.createPlayer();
    
    // Load justice quotes and spawn NPCs
    this.spawnJustices();

    this.createDoors();
    this.addDecorations();
    this.setupInput();
    this.setupCollisions();
    this.setupCamera();
    this.createUI();

    // Play background music if enabled
    if (profileStore.isAudioEnabled() && !this.sound.get('courtroom')?.isPlaying) {
      this.sound.play('courtroom', { loop: true, volume: 0.3 });
    }

    // Listen for audio toggle events from React settings
    window.addEventListener('audio-toggle', this.handleAudioToggle);
    window.addEventListener('audio-volume', this.handleVolumeChange);

    // If running in E2E test mode, expose test helpers to control doors
    if ((window as any).__E2E_TEST_MODE) {
      this.time.delayedCall(150, () => {
        const mapping: Record<string, { x: number; y: number }> = {};
        for (const door of this.doors) {
          mapping[door.subject] = {
            x: Math.round(door.visual.x - this.cameras.main.scrollX),
            y: Math.round(door.visual.y - this.cameras.main.scrollY),
          };
        }
        (window as any).__TEST_DOORS = mapping;
        (window as any).__TEST_FIRST_DOOR_SUBJECT = this.doors[0]?.subject;

        // Directly enter a door by subject name (used by E2E tests)
        (window as any).__enterDoor = (subject: string) => {
          const d = this.doors.find((x) => x.subject === subject);
          if (!d) return false;
          this.enterDoor(d);
          return true;
        };
      });
    }
  }

  private handleAudioToggle = (event: Event) => {
    const customEvent = event as CustomEvent<{ enabled: boolean }>;
    this.sound.mute = !customEvent.detail.enabled;

    // Expose mute state for E2E verification
    if ((window as any).__E2E_TEST_MODE) (window as any).__SOUND_MUTED = this.sound.mute;
  };

  private handleVolumeChange = (event: Event) => {
    const customEvent = event as CustomEvent<{ volume: number }>;
    this.sound.volume = customEvent.detail.volume;
  };

  /**
   * Spawn Supreme Court Justice NPCs with their real quotes
   */
  private spawnJustices(): void {
    // Justice positions on the hub map
    const justicePositions = [
      { id: 'john_roberts', x: 15, y: 5 }, // Chief Justice at top center
      { id: 'clarence_thomas', x: 13, y: 5 },
      { id: 'samuel_alito', x: 17, y: 5 },
      { id: 'sonia_sotomayor', x: 11, y: 6 },
      { id: 'elena_kagan', x: 19, y: 6 },
      { id: 'neil_gorsuch', x: 9, y: 7 },
      { id: 'brett_kavanaugh', x: 21, y: 7 },
      { id: 'amy_coney_barrett', x: 7, y: 8 },
    ];
    
    // Load justice quotes from cache
    const quotesData = this.cache.json.get('justice-quotes');
    const quotesMap = new Map<string, string[]>();
    
    if (quotesData?.justices) {
      quotesData.justices.forEach((justice: JusticeQuoteData) => {
        // Normalize name to id format (e.g., "John G. Roberts, Jr." -> "john_roberts")
        const normalizedId = this.normalizeJusticeName(justice.name);
        const quotes = justice.quotes.map((q: { text: string }) => q.text);
        quotesMap.set(normalizedId, quotes);
      });
    }

    const offsetX = this.data.get('offsetX');
    const offsetY = this.data.get('offsetY');

    justicePositions.forEach(position => {
      const npc = Spawner.spawnNPC(
        this,
        offsetX + position.x * SCALED_TILE + SCALED_TILE / 2,
        offsetY + position.y * SCALED_TILE + SCALED_TILE / 2,
        position.id
      );
      
      if (npc) {
        npc.setScale(SCALE * 0.12); // Adjust scale for huge sprites
        this.entityLayer.add(npc);
        
        // Make NPC interactive
        npc.setInteractive({ useHandCursor: true });
        
        // Get quotes for this justice
        const quotes = quotesMap.get(position.id) || ['No quotes available.'];
        const displayName = this.getJusticeDisplayName(position.id);
        
        const justiceNPC: JusticeNPC = {
          id: position.id,
          name: displayName,
          sprite: npc,
          quotes,
          // Store home position for wandering
          homeX: npc.x,
          homeY: npc.y,
          isWandering: false
        };
        
        this.justiceNPCs.push(justiceNPC);
        
        // Start wandering behavior with random delay
        this.startJusticeWander(justiceNPC, Math.random() * 3000);
        
        // Click to interact
        npc.on('pointerdown', () => {
          this.interactWithJustice(justiceNPC);
        });
        
        // Hover effect
        npc.on('pointerover', () => {
          npc.setTint(0xaaaaff);
        });
        npc.on('pointerout', () => {
          npc.clearTint();
        });
      }
    });
    
    console.info(`[WorldScene] Spawned ${this.justiceNPCs.length} Justice NPCs`);
  }
  
  /**
   * Normalize justice name from JSON to internal ID format
   */
  private normalizeJusticeName(name: string): string {
    // Map full names to internal IDs
    const nameMap: Record<string, string> = {
      'john g. roberts, jr.': 'john_roberts',
      'clarence thomas': 'clarence_thomas',
      'samuel a. alito, jr.': 'samuel_alito',
      'sonia sotomayor': 'sonia_sotomayor',
      'elena kagan': 'elena_kagan',
      'neil m. gorsuch': 'neil_gorsuch',
      'brett m. kavanaugh': 'brett_kavanaugh',
      'amy coney barrett': 'amy_coney_barrett',
      'ketanji brown jackson': 'ketanji_jackson', // Not spawned but might be in quotes
    };
    return nameMap[name.toLowerCase()] || name.toLowerCase().replace(/[^a-z]/g, '_');
  }
  
  /**
   * Get display name for a justice ID
   */
  private getJusticeDisplayName(id: string): string {
    const displayNames: Record<string, string> = {
      'john_roberts': 'Chief Justice Roberts',
      'clarence_thomas': 'Justice Thomas',
      'samuel_alito': 'Justice Alito',
      'sonia_sotomayor': 'Justice Sotomayor',
      'elena_kagan': 'Justice Kagan',
      'neil_gorsuch': 'Justice Gorsuch',
      'brett_kavanaugh': 'Justice Kavanaugh',
      'amy_coney_barrett': 'Justice Barrett',
    };
    return displayNames[id] || id;
  }
  
  /**
   * Interact with a Justice NPC - show a random quote
   */
  private interactWithJustice(justice: JusticeNPC): void {
    // Don't interact if already in dialog
    if (this.dialogManager.isActive()) return;
    
    // Pick a random quote
    const randomQuote = justice.quotes[Math.floor(Math.random() * justice.quotes.length)];
    
    this.dialogManager.say(justice.name, randomQuote);
  }
  
  /**
   * Start wandering behavior for a justice NPC
   */
  private startJusticeWander(justice: JusticeNPC, delay: number = 0): void {
    // Clear existing timer if any
    if (justice.wanderTimer) {
      justice.wanderTimer.destroy();
    }
    
    // Random interval between wanders (3-8 seconds)
    const interval = 3000 + Math.random() * 5000;
    
    justice.wanderTimer = this.time.addEvent({
      delay: delay || interval,
      callback: () => this.wanderJustice(justice),
      callbackScope: this,
      loop: false
    });
  }
  
  /**
   * Make a justice NPC wander to a nearby random position
   */
  private wanderJustice(justice: JusticeNPC): void {
    if (!justice.sprite || !justice.sprite.active) return;
    
    // Don't wander during dialog
    if (this.dialogManager?.isActive()) {
      this.startJusticeWander(justice, 2000);
      return;
    }
    
    const sprite = justice.sprite;
    const homeX = justice.homeX ?? sprite.x;
    const homeY = justice.homeY ?? sprite.y;
    
    // Random offset from home position (within ~64 pixels)
    const wanderRadius = 64;
    const angle = Math.random() * Math.PI * 2;
    const distance = Math.random() * wanderRadius;
    
    const targetX = homeX + Math.cos(angle) * distance;
    const targetY = homeY + Math.sin(angle) * distance;
    
    // Clamp to reasonable bounds
    const minX = 100;
    const maxX = GAME_WIDTH - 100;
    const minY = 100;
    const maxY = GAME_HEIGHT - 100;
    
    justice.targetX = Phaser.Math.Clamp(targetX, minX, maxX);
    justice.targetY = Phaser.Math.Clamp(targetY, minY, maxY);
    justice.isWandering = true;
    
    // Determine walk direction for animation
    const dx = justice.targetX - sprite.x;
    const dy = justice.targetY - sprite.y;
    const direction = Math.abs(dx) > Math.abs(dy) 
      ? (dx > 0 ? 'right' : 'left')
      : (dy > 0 ? 'down' : 'up');
    
    // Flip sprite based on horizontal direction
    if (direction === 'left') {
      sprite.setFlipX(true);
    } else if (direction === 'right') {
      sprite.setFlipX(false);
    }
    
    // Tween the sprite to the target position
    const travelTime = 800 + Math.random() * 600;
    
    this.tweens.add({
      targets: sprite,
      x: justice.targetX,
      y: justice.targetY,
      duration: travelTime,
      ease: 'Quad.easeInOut',
      onComplete: () => {
        justice.isWandering = false;
        // Schedule next wander
        this.startJusticeWander(justice);
      }
    });
    
    // Add a subtle bobbing animation while walking
    this.tweens.add({
      targets: sprite,
      scaleY: sprite.scaleY * 0.98,
      duration: 150,
      yoyo: true,
      repeat: Math.floor(travelTime / 300),
      ease: 'Quad.easeInOut'
    });
  }
  
  /**
   * Find a justice NPC within interaction range of the player
   */
  private findNearbyJustice(): JusticeNPC | null {
    const interactionRange = 50; // pixels
    
    for (const justice of this.justiceNPCs) {
      const distance = Phaser.Math.Distance.Between(
        this.player.x,
        this.player.y,
        justice.sprite.x,
        justice.sprite.y
      );
      
      if (distance < interactionRange) {
        return justice;
      }
    }
    
    return null;
  }

  private createTilemap(): void {
    // Create wall collision group
    this.walls = this.physics.add.staticGroup();

    // Parse map data and create tiles
    const { width, height, layers } = this.mapData;

    // Calculate offset to center the map
    const mapPixelWidth = width * SCALED_TILE;
    const mapPixelHeight = height * SCALED_TILE;
    const offsetX = (GAME_WIDTH - mapPixelWidth) / 2;
    const offsetY = (GAME_HEIGHT - mapPixelHeight) / 2;

    const collisionLayerData = layers.find((l: any) => l.name === 'collision')?.data;

    // Floor Tiles by Zone: Supreme Court interior office style
    // - Supreme Court area (top): Wood flooring - elegant judicial office
    // - Middle transition: Carpet flooring - formal office corridors  
    // - Library/study area (bottom): Carpet - distinguished chambers
    const woodFloor = [
      WORLD_TILES.FLOOR_WOOD_A,
      WORLD_TILES.FLOOR_WOOD_B,
      WORLD_TILES.FLOOR_WOOD_C,
    ];
    const carpetFloor = [
      WORLD_TILES.FLOOR_CARPET_A,
      WORLD_TILES.FLOOR_CARPET_B,
      WORLD_TILES.FLOOR_CARPET_C,
    ];
    const richCarpetFloor = [
      WORLD_TILES.FLOOR_CARPET_D,
      WORLD_TILES.FLOOR_CARPET_E,
      WORLD_TILES.FLOOR_CARPET_A,
    ];

    // Zone boundaries (adjust based on map height)
    const supremeCourtZone = Math.floor(height * 0.35); // Top ~35% is Supreme Court
    const transitionZone = Math.floor(height * 0.5);     // Middle ~15% is transition

    // Fill the floor with contextual tiles
    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        // Skip if collision tile (wall)
        if (collisionLayerData && collisionLayerData[y * width + x] === 0) {
           // Select floor type based on zone - all interior office style
           let floorTiles: number[];
           if (y <= supremeCourtZone) {
             // Supreme Court area - elegant wood flooring
             floorTiles = woodFloor;
           } else if (y <= transitionZone) {
             // Transition/office corridors - carpet flooring
             floorTiles = carpetFloor;
           } else {
             // Chambers/study area - rich carpet flooring
             floorTiles = richCarpetFloor;
           }

           // Checkerboard pattern for visual interest
           const floorTile = (x + y) % 2 === 0 ? floorTiles[0] : floorTiles[1];
           const sprite = this.add.sprite(
             offsetX + x * SCALED_TILE + SCALED_TILE / 2,
             offsetY + y * SCALED_TILE + SCALED_TILE / 2,
             'world',
             floorTile
           );
           sprite.setScale(SCALE);
           this.floorLayer.add(sprite);
        }
      }
    }

    // Decorate: Bookshelves lining the top walls (and some sides)
    // We skip where doors might be (though doors are usually in the middle)
    for (let x = 1; x < width - 1; x++) {
      if (x % 3 !== 0) { // Leave some gaps
        this.addProp(offsetX, offsetY, x, 1, WORLD_TILES.BOOKSHELF_A);
      }
    }

    // Add decorative rugs in the Supreme Court zone for office feel
    for (let y = 2; y < supremeCourtZone; y += 3) {
      for (let x = 3; x < width - 3; x += 5) {
        if (Math.random() > 0.3) {
          this.addProp(offsetX, offsetY, x, y, WORLD_TILES.RUG_A);
        }
      }
    }

    // Add candles/lamps along walls for office lighting ambiance
    for (let y = 3; y < height - 2; y += 4) {
      // Left wall candles
      this.addProp(offsetX, offsetY, 1.5, y, WORLD_TILES.CANDLES_A);
      // Right wall candles
      this.addProp(offsetX, offsetY, width - 2.5, y, WORLD_TILES.CANDLES_B);
    }
    
    // Add study tables in the gaps between door rows
    // Doors are roughly at Y=3, Y=12, Y=21
    const studyRows = [7, 8, 16, 17];
    const clutterItems = ['law_book_red', 'law_book_blue', 'coffee', 'legal_brief', 'gavel', 'ink_bottle', 'scales', 'briefcase'];

    for (const y of studyRows) {
      for (let x = 4; x < width - 4; x += 4) {
        // Table
        this.addProp(offsetX, offsetY, x, y, WORLD_TILES.TABLE);
        // Chair left
        this.addProp(offsetX, offsetY, x - 0.5, y, WORLD_TILES.CHAIR);
        
        // Random Clutter
        if (Math.random() > 0.4) {
           const item = randomElement(clutterItems);
           this.addMappedProp(offsetX, offsetY, x, y - 0.2, item);
        }
      }
    }

    // Wall tile variants
    const wallHorizontal = [
      WORLD_TILES.WALL_STONE_H_A,
      WORLD_TILES.WALL_STONE_H_B,
    ];
    const wallVertical = [
      WORLD_TILES.WALL_STONE_V_A,
      WORLD_TILES.WALL_STONE_V_B,
    ];

    // Create collision walls (Outer Ring)
    if (collisionLayerData) {
      for (let y = 0; y < height; y++) {
        for (let x = 0; x < width; x++) {
          if (collisionLayerData[y * width + x] > 0) {
            const isTopOrBottom = y === 0 || y === height - 1;
            const wallTile = isTopOrBottom
              ? randomElement(wallHorizontal)
              : randomElement(wallVertical);

            const sprite = this.add.sprite(
              offsetX + x * SCALED_TILE + SCALED_TILE / 2,
              offsetY + y * SCALED_TILE + SCALED_TILE / 2,
              'world',
              wallTile
            );
            sprite.setScale(SCALE);
            this.propLayer.add(sprite);

            const wall = this.add.rectangle(
              offsetX + x * SCALED_TILE + SCALED_TILE / 2,
              offsetY + y * SCALED_TILE + SCALED_TILE / 2,
              SCALED_TILE,
              SCALED_TILE,
              0x000000, 0
            );
            this.physics.add.existing(wall, true);
            this.walls.add(wall);
          }
        }
      }
    }

    // Store offset for player positioning
    this.data.set('offsetX', offsetX);
    this.data.set('offsetY', offsetY);
  }

  private addMappedProp(offsetX: number, offsetY: number, tileX: number, tileY: number, key: string): void {
    const mapping = PROPS_MAPPING[key];
    if (!mapping) return;

    const sprite = this.add.sprite(
      offsetX + tileX * SCALED_TILE + SCALED_TILE / 2,
      offsetY + tileY * SCALED_TILE + SCALED_TILE / 2,
      mapping.atlas,
      mapping.frame
    );
    sprite.setScale(mapping.scale || SCALE);
    if (mapping.yOffset) {
      sprite.y += mapping.yOffset;
    }
    this.propLayer.add(sprite);
  }

  private addProp(offsetX: number, offsetY: number, tileX: number, tileY: number, frame: number): void {
     const override = TILE_OVERRIDES[frame];
     let sprite: Phaser.GameObjects.Sprite;

     if (override) {
       sprite = this.add.sprite(
         offsetX + tileX * SCALED_TILE + SCALED_TILE / 2,
         offsetY + tileY * SCALED_TILE + SCALED_TILE / 2,
         override.atlas,
         override.frame
       );
       sprite.setScale(override.scale || SCALE);
       if (override.yOffset) {
         sprite.y += override.yOffset;
       }
     } else {
       sprite = this.add.sprite(
          offsetX + tileX * SCALED_TILE + SCALED_TILE / 2,
          offsetY + tileY * SCALED_TILE + SCALED_TILE / 2,
          'world',
          frame
       );
       sprite.setScale(SCALE);
     }
     
     this.propLayer.add(sprite);
  }

  private createPlayer(): void {
    const offsetX = this.data.get('offsetX');
    const offsetY = this.data.get('offsetY');

    const objectsLayer = this.mapData.layers.find((l: any) => l.name === 'objects');
    let spawnX = 15;
    let spawnY = 22;

    if (objectsLayer?.objects) {
      const spawn = objectsLayer.objects.find((o: any) => o.name === 'player_spawn');
      if (spawn) {
        spawnX = Math.floor(spawn.x / TILE_SIZE);
        spawnY = Math.floor(spawn.y / TILE_SIZE);
      }
    }

    const charOption = getCharacterOption(this.charId);

    // Kim uses high-res atlas (192x272 frames), others use 16x16 spritesheets
    const isHighResAtlas = charOption.texture.startsWith('k1_kim');

    if (isHighResAtlas) {
      // For Kim's atlas, use first frame name and scale down to fit world
      const firstFrame = `${charOption.texture}_0001`;
      this.player = this.add.sprite(
        offsetX + spawnX * SCALED_TILE + SCALED_TILE / 2,
        offsetY + spawnY * SCALED_TILE + SCALED_TILE / 2,
        charOption.texture,
        firstFrame
      );
      // Scale down 192x272 to roughly 32x45 (maintain aspect ratio)
      // 32 / 192 = 0.167
      this.player.setScale(0.2);
    } else {
      this.player = this.add.sprite(
        offsetX + spawnX * SCALED_TILE + SCALED_TILE / 2,
        offsetY + spawnY * SCALED_TILE + SCALED_TILE / 2,
        charOption.texture || 'monsters',
        charOption.frame
      );
      this.player.setScale(SCALE);
    }
    this.entityLayer.add(this.player);

    this.physics.add.existing(this.player);
    const body = this.player.body as Phaser.Physics.Arcade.Body;
    body.setCollideWorldBounds(true);
    body.setSize(TILE_SIZE * SCALE * 0.75, TILE_SIZE * SCALE * 0.75);
    body.setOffset(TILE_SIZE * SCALE * 0.125, TILE_SIZE * SCALE * 0.125);

    this.player.play(`${this.charId}-idle`);
  }

  private createDoors(): void {
    const offsetX = this.data.get('offsetX');
    const offsetY = this.data.get('offsetY');

    const objectsLayer = this.mapData.layers.find((l: any) => l.name === 'objects');
    if (!objectsLayer?.objects) return;

    const doorObjects = objectsLayer.objects.filter((o: any) => o.name === 'door');

    for (const doorObj of doorObjects) {
      const doorX = doorObj.x + TILE_SIZE / 2;
      const doorY = doorObj.y + TILE_SIZE / 2;
      const subject = doorObj.properties?.subject || 'Unknown';
      const colorStr = doorObj.properties?.color || '0xffaa00';
      const color = parseInt(colorStr.replace('0x', ''), 16);
      const theme = SUBJECT_THEMES[subject];
      const doorTile = theme?.door || WORLD_TILES.DOOR_STONE_V_CLOSED;

      // Rug under each door for emphasis
      const rug = this.add.sprite(
        offsetX + doorX * SCALE,
        offsetY + doorY * SCALE + SCALED_TILE / 2,
        'world',
        WORLD_TILES.RUG_A
      );
      rug.setScale(SCALE);
      this.floorLayer.add(rug);

      const doorSprite = this.add.sprite(
        offsetX + doorX * SCALE,
        offsetY + doorY * SCALE,
        'world',
        doorTile
      );
      doorSprite.setScale(SCALE);
      doorSprite.setTint(color);
      this.propLayer.add(doorSprite);

      const shortLabel = this.shortenSubject(subject);
      const label = this.add.text(
        offsetX + doorX * SCALE,
        offsetY + doorY * SCALE - SCALED_TILE * 0.8,
        shortLabel,
        {
          fontSize: '10px',
          color: '#ffffff',
          fontFamily: 'Arial',
          backgroundColor: '#000000aa',
          padding: { x: 4, y: 2 },
        }
      ).setOrigin(0.5);
      label.setDepth(1000);

      const zone = this.add.zone(
        offsetX + doorX * SCALE,
        offsetY + doorY * SCALE,
        SCALED_TILE * 3,
        SCALED_TILE * 3
      );
      this.physics.add.existing(zone);
      (zone.body as Phaser.Physics.Arcade.Body).setAllowGravity(false);

      const doorData: DoorData = { x: doorX, y: doorY, subject, color, zone, visual: doorSprite, label };
      this.doors.push(doorData);

      this.physics.add.overlap(
        this.player,
        zone,
        () => this.onDoorProximity(doorData),
        undefined,
        this
      );
    }
  }

  /**
   * Add decorative props to the hub
   */
  private addDecorations(): void {
    // This is now intentionally left blank to avoid the overly complex decorations.
    // The base map from Tiled should contain the primary decorative elements.
  }

  private shortenSubject(subject: string): string {
    const shortNames: Record<string, string> = {
      'Civil Procedure': 'Civ Pro',
      'Con Law': 'Con Law',
      'Conflicts Of Law': 'Conflicts',
      'Criminal Law': 'Crim Law',
      'Criminal Procedure': 'Crim Pro',
      'Family Law': 'Family',
      'Real Property': 'Property',
      'Secured Transactions': 'Secured',
      'Trusts And Estates': 'Trusts',
    };
    return shortNames[subject] || subject;
  }

  private setupInput(): void {
    this.cursors = this.input.keyboard!.createCursorKeys();

    // Add WASD support
    this.input.keyboard!.addKeys('W,A,S,D');

    // Add Enter key to enter doors
    this.input.keyboard!.on('keydown-ENTER', () => {
      if (this.activeDoor && this.canEnterDoor) {
        this.enterDoor(this.activeDoor);
      }
    });

    // Add keybind to switch to Asset Lab
    this.input.keyboard!.on('keydown-L', () => {
      this.scene.switch('AssetLabScene');
    });

    // Add Space key as alternative - also checks for nearby justices
    this.input.keyboard!.on('keydown-SPACE', () => {
      // First check if dialog is active
      if (this.dialogManager.isActive()) return;
      
      // Check for nearby justice to interact with
      const nearbyJustice = this.findNearbyJustice();
      if (nearbyJustice) {
        this.interactWithJustice(nearbyJustice);
        return;
      }
      
      // Otherwise try to enter door
      if (this.activeDoor && this.canEnterDoor) {
        this.enterDoor(this.activeDoor);
      }
    });

    // Make doors directly clickable
    this.input.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
      if (!this.canEnterDoor) return;

      // Convert screen pointer to world coordinates (accounting for camera scroll)
      const worldPoint = this.cameras.main.getWorldPoint(pointer.x, pointer.y);

      // Check if pointer is over any door
      for (const door of this.doors) {
        const distance = Phaser.Math.Distance.Between(
          worldPoint.x,
          worldPoint.y,
          door.visual.x,
          door.visual.y
        );

        // If within 60 pixels (1.5x the door size), activate
        if (distance < 60) {
          this.enterDoor(door);
          return;
        }
      }
    });
  }

  private setupCollisions(): void {
    // Player-wall collision
    this.physics.add.collider(this.player, this.walls);
  }

  private setupCamera(): void {
    const offsetX = this.data.get('offsetX') as number;
    const offsetY = this.data.get('offsetY') as number;
    const { width, height } = this.mapData;

    // Calculate map bounds in world coordinates
    const mapWidth = width * SCALED_TILE;
    const mapHeight = height * SCALED_TILE;

    // Set physics world bounds to match the map (so player can move to edges)
    this.physics.world.setBounds(offsetX, offsetY, mapWidth, mapHeight);

    // Set camera bounds to the map area
    this.cameras.main.setBounds(
      offsetX,
      offsetY,
      mapWidth,
      mapHeight
    );

    // Make camera follow the player
    this.cameras.main.startFollow(this.player, true, 0.1, 0.1);

    // Set a dead zone so camera doesn't jitter on small movements
    this.cameras.main.setDeadzone(100, 80);
  }

  private createUI(): void {
    // Instructions at bottom - scrollFactor(0) keeps it fixed on screen
    this.add.text(GAME_WIDTH / 2, GAME_HEIGHT - 20, 'Arrow keys/WASD to move. Press ENTER or SPACE near a door to start a run. [L] Asset Lab', {
      fontSize: '11px',
      color: '#aaaaaa',
      fontFamily: 'Arial',
    }).setOrigin(0.5).setDepth(1000).setScrollFactor(0);

    // Add title below HUD bar (HUD takes ~45px at top)
    this.add.text(GAME_WIDTH / 2, 55, 'KIM BARDASHIAN: FINAL CHANCE', {
      fontSize: '16px',
      color: '#ffcc00',
      fontFamily: 'Arial',
      fontStyle: 'bold',
    }).setOrigin(0.5).setDepth(1000).setScrollFactor(0);
  }

  private onDoorProximity(door: DoorData): void {
    if (this.activeDoor !== door) {
      // Emit door proximity event for React
      if (this.activeDoor) {
        gameBridge.emit('door-proximity', { subject: this.activeDoor.subject, near: false });
        // Reset previous door visual
        this.activeDoor.visual.clearTint();
        this.activeDoor.visual.setTint(this.activeDoor.color);
      }

      this.activeDoor = door;

      // Highlight active door (brighten tint)
      door.visual.setTint(0xffffff);

      // Scale up monster preview
      if (door.monster) {
        this.tweens.add({
          targets: door.monster,
          scaleX: SCALE,
          scaleY: SCALE,
          duration: 200,
          ease: 'Back.easeOut',
        });
      }

      // Emit door proximity event
      gameBridge.emit('door-proximity', { subject: door.subject, near: true });

      // Show prompt
      this.showDoorPrompt(door);
    }
  }

  private showDoorPrompt(door: DoorData): void {
    // Remove any existing prompt
    const existingPrompt = this.children.getByName('doorPrompt');
    if (existingPrompt) existingPrompt.destroy();

    this.add.text(
      GAME_WIDTH / 2,
      GAME_HEIGHT - 50,
      `Press ENTER to study ${door.subject}`,
      {
        fontSize: '14px',
        color: '#ffff00',
        fontFamily: 'Arial',
        backgroundColor: '#000000aa',
        padding: { x: 10, y: 5 },
      }
    ).setOrigin(0.5).setName('doorPrompt').setDepth(1000).setScrollFactor(0);
  }

  private enterDoor(door: DoorData): void {
    if (!this.canEnterDoor) return;
    this.canEnterDoor = false;

    // Stop player movement
    const body = this.player.body as Phaser.Physics.Arcade.Body;
    body.setVelocity(0, 0);
    this.player.play('player-idle');

    // Door opening animation
    this.tweens.add({
      targets: door.visual,
      scaleX: SCALE * 1.2,
      scaleY: SCALE * 1.2,
      alpha: 0,
      duration: 300,
      ease: 'Power2',
    });

    // Player walks through door
    this.tweens.add({
      targets: this.player,
      x: door.visual.x,
      y: door.visual.y,
      alpha: 0,
      duration: 400,
      ease: 'Power2',
      onComplete: () => {
        this.startRun(door);
      },
    });

    // Emit door entered event
    gameBridge.emit('door-entered', { subject: door.subject });
  }

  private startRun(door: DoorData): void {
    // Convert subject name to dungeon ID (lowercase, hyphenated)
    const dungeonId = this.subjectToDungeonId(door.subject);

    // Emit door entered event
    gameBridge.emit('run-started', {
      subject: door.subject,
      dungeonId: dungeonId,
    });

    // Transition to dungeon scene
    this.scene.start('DungeonScene', {
      subject: door.subject,
      dungeonId: dungeonId,
    });
  }

  /**
   * Convert subject name to dungeon JSON ID
   */
  private subjectToDungeonId(subject: string): string {
    const mapping: Record<string, string> = {
      'Agency': 'agency',
      'Civil Procedure': 'civil-procedure',
      'Con Law': 'con-law',
      'Conflicts Of Law': 'conflicts-of-law',
      'Contracts': 'contracts',
      'Corporations': 'corporations',
      'Criminal Law': 'criminal-law',
      'Criminal Procedure': 'criminal-procedure',
      'Evidence': 'evidence',
      'Family Law': 'family-law',
      'Partnerships': 'partnerships',
      'Real Property': 'real-property',
      'Secured Transactions': 'secured-transactions',
      'Torts': 'torts',
      'Trusts And Estates': 'trusts-and-estates',
    };
    return mapping[subject] || subject.toLowerCase().replace(/\s+/g, '-');
  }

  update(): void {
    // Don't allow movement while dialog is active
    if (this.dialogManager?.isActive()) {
      const body = this.player.body as Phaser.Physics.Arcade.Body;
      body.setVelocity(0);
      return;
    }
    
    const body = this.player.body as Phaser.Physics.Arcade.Body;
    const keys = this.input.keyboard!.addKeys('W,A,S,D') as any;

    // Reset velocity
    body.setVelocity(0);

    let isMoving = false;
    let newDirection: 'up' | 'down' | 'left' | 'right' = this.lastDirection;

    // Horizontal movement
    if (this.cursors.left.isDown || keys.A.isDown) {
      body.setVelocityX(-this.playerSpeed);
      newDirection = 'left';
      isMoving = true;
    } else if (this.cursors.right.isDown || keys.D.isDown) {
      body.setVelocityX(this.playerSpeed);
      newDirection = 'right';
      isMoving = true;
    }

    // Vertical movement
    if (this.cursors.up.isDown || keys.W.isDown) {
      body.setVelocityY(-this.playerSpeed);
      newDirection = 'up';
      isMoving = true;
    } else if (this.cursors.down.isDown || keys.S.isDown) {
      body.setVelocityY(this.playerSpeed);
      newDirection = 'down';
      isMoving = true;
    }

    // Normalize diagonal movement
    if (body.velocity.x !== 0 && body.velocity.y !== 0) {
      body.velocity.normalize().scale(this.playerSpeed);
    }

    // Update animation based on movement
    if (isMoving) {
      const animKey = `${this.charId}-walk-${newDirection}`;
      if (newDirection !== this.lastDirection || !this.player.anims.isPlaying || this.player.anims.currentAnim?.key !== animKey) {
        this.player.play(animKey, true);
        this.lastDirection = newDirection;
      }
    } else {
      // Stop walking animation, show idle
      if (this.player.anims.isPlaying) {
        this.player.stop();
        // Play the idle animation to ensure a consistent resting state
        this.player.play(`${this.charId}-idle`);
      }
    }

    // Check if player moved away from active door
    if (this.activeDoor) {
      const distance = Phaser.Math.Distance.Between(
        this.player.x, this.player.y,
        this.activeDoor.zone.x, this.activeDoor.zone.y
      );
      if (distance > SCALED_TILE * 3) {
        // Emit door proximity event
        gameBridge.emit('door-proximity', { subject: this.activeDoor.subject, near: false });

        // Reset door visual
        this.activeDoor.visual.setTint(this.activeDoor.color);

        // Scale down monster preview
        if (this.activeDoor.monster) {
          this.tweens.add({
            targets: this.activeDoor.monster,
            scaleX: SCALE * 0.75,
            scaleY: SCALE * 0.75,
            duration: 200,
          });
        }

        this.activeDoor = null;
        this.canEnterDoor = true;

        // Remove prompt
        const prompt = this.children.getByName('doorPrompt');
        if (prompt) prompt.destroy();
      }
    }
  }

  shutdown(): void {
    // Remove event listeners
    window.removeEventListener('audio-toggle', this.handleAudioToggle);
    window.removeEventListener('audio-volume', this.handleVolumeChange);
  }
}
